// Shared clip-share helper. Tries Web Share API (best on mobile) with
// a file attachment first; falls back to clipboard-copy of the URL +
// a direct download anchor for desktop. The caller passes in the
// blob URL and a human-readable share string, plus a label-updater so
// the host button can flash success/fail feedback without knowing
// about the underlying mechanics.

export type ShareClipInput = {
  blobUrl: string;
  filename: string;
  shareText: string;
  pageUrl?: string;
  // Optional UI callback for flash feedback: called with message + hex
  // color. The host button typically maps this to its own label.
  onStatus?: (msg: string, color: string) => void;
};

type NavShare = Navigator & {
  canShare?: (d: { files?: File[] }) => boolean;
  share?: (d: { files?: File[]; text?: string; url?: string; title?: string }) => Promise<void>;
};

export async function shareClip(input: ShareClipInput): Promise<void> {
  const { blobUrl, filename, shareText, onStatus } = input;
  const pageUrl = input.pageUrl ?? (typeof location !== 'undefined' ? location.href : '');
  // Try native share with attached file — ideal on mobile.
  try {
    const blob = await fetch(blobUrl).then((r) => r.blob());
    const file = new File([blob], filename, { type: blob.type });
    const nav = navigator as NavShare;
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      await nav.share({ files: [file], text: shareText, url: pageUrl, title: 'Game clip' });
      onStatus?.('SHARED ✓', '#86efac');
      return;
    }
  } catch (err) {
    console.info('[share] native share unavailable', err);
  }
  // Desktop fallback: copy link + trigger download.
  try {
    await navigator.clipboard.writeText(`${shareText} ${pageUrl}`.trim());
    onStatus?.('LINK COPIED', '#86efac');
  } catch {
    onStatus?.('HOLD TO SAVE', '#fde047');
  }
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
}
